import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  add,
  addOutline,
  alertCircleOutline,
  arrowBackOutline,
  arrowForward,
  backspaceOutline,
  bookOutline,
  businessOutline,
  calendarOutline,
  callOutline,
  cameraOutline,
  cardOutline,
  cashOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  chevronBackOutline,
  chevronForwardOutline,
  close,
  closeCircleOutline,
  codeOutline,
  copyOutline,
  documentTextOutline,
  downloadOutline,
  fingerPrintOutline,
  globeOutline,
  idCardOutline,
  informationCircleOutline,
  keyOutline,
  languageOutline,
  locationOutline,
  lockClosedOutline,
  logOutOutline,
  logoInstagram,
  logoLinkedin,
  logoWhatsapp,
  mailOutline,
  mapOutline,
  moonOutline,
  notificationsOutline,
  openOutline,
  personCircleOutline,
  personOutline,
  pinOutline,
  qrCodeOutline,
  receiptOutline,
  schoolOutline,
  searchOutline,
  shareOutline,
  shieldCheckmarkOutline,
  shieldOutline,
  starOutline,
  timeOutline,
  trashOutline,
  videocamOutline,
  walletOutline,
  warningOutline,
  cloudOfflineOutline,
  filterOutline,
  refreshCircle,
  sunnyOutline,
  phonePortraitOutline,
  closeCircle,
  helpCircle,
  helpCircleOutline,
  hourglassOutline,
  eyeOutline,
  eyeOffOutline,
  homeOutline,
  settingsOutline,
  notificationsOffOutline,
  home,
  card,
  time,
  ellipsisHorizontal,
  ellipsisHorizontalOutline,
  scan,
  scanOutline,
  wifiOutline,
  refreshOutline,
  chevronExpandOutline,
} from 'ionicons/icons';

import { OfflineBannerComponent } from './components/offline-banner/offline-banner.component';
import { PinEntryComponent } from './components/pin-entry/pin-entry.component';
import { TranslateService } from './core/i18n';
import { LocalAuthService } from './public/auth/services/local-auth.service';
import { AuthStore } from './public/auth/store/auth.store';
import { AppLockService } from './services/app-lock.service';
import { DeepLinkService } from './services/deep-link.service';
import { PushNotificationService } from './services/push-notification.service';
import { ThemeService } from './services/theme.service';

// Register all icons globally
addIcons({
  add,
  addOutline,
  backspaceOutline,
  alertCircleOutline,
  arrowBackOutline,
  arrowForward,
  bookOutline,
  businessOutline,
  calendarOutline,
  callOutline,
  cameraOutline,
  cardOutline,
  cashOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  chevronBackOutline,
  chevronForwardOutline,
  close,
  closeCircleOutline,
  codeOutline,
  copyOutline,
  documentTextOutline,
  downloadOutline,
  fingerPrintOutline,
  globeOutline,
  idCardOutline,
  informationCircleOutline,
  keyOutline,
  languageOutline,
  locationOutline,
  lockClosedOutline,
  logOutOutline,
  logoInstagram,
  logoLinkedin,
  logoWhatsapp,
  mailOutline,
  mapOutline,
  moonOutline,
  notificationsOutline,
  openOutline,
  personCircleOutline,
  personOutline,
  pinOutline,
  qrCodeOutline,
  receiptOutline,
  schoolOutline,
  searchOutline,
  shareOutline,
  shieldCheckmarkOutline,
  shieldOutline,
  starOutline,
  timeOutline,
  trashOutline,
  videocamOutline,
  walletOutline,
  warningOutline,
  cloudOfflineOutline,
  filterOutline,
  refreshCircle,
  sunnyOutline,
  phonePortraitOutline,
  closeCircle,
  helpCircle,
  helpCircleOutline,
  hourglassOutline,
  eyeOutline,
  eyeOffOutline,
  homeOutline,
  settingsOutline,
  notificationsOffOutline,
  home,
  card,
  time,
  ellipsisHorizontal,
  ellipsisHorizontalOutline,
  scan,
  scanOutline,
  wifiOutline,
  refreshOutline,
  chevronExpandOutline,
});

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [CommonModule, IonApp, IonRouterOutlet, OfflineBannerComponent, PinEntryComponent],
})
export class AppComponent {
  private readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  private readonly pushService = inject(PushNotificationService);
  private readonly deepLinkService = inject(DeepLinkService);
  readonly authStore = inject(AuthStore);
  readonly appLockService = inject(AppLockService);
  private readonly localAuth = inject(LocalAuthService);

  constructor() {
    this.themeService.init();
    this.deepLinkService.initialize();

    // Initialize app lock service when auth state is restored
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        void this.initPushNotifications();
        void this.initAppLock();
      }
    });
  }

  onUnlocked(): void {
    this.appLockService.unlock();
  }

  onForgotPin(): void {
    void this.localAuth.forgotPin();
    this.appLockService.unlock();
  }

  private async initPushNotifications(): Promise<void> {
    await this.pushService.initialize();
    await this.pushService.checkPermissionStatus();
  }

  private async initAppLock(): Promise<void> {
    const pinConfigured = await this.localAuth.isPinConfigured();
    await this.appLockService.initialize(pinConfigured);
  }
}
